import { useState, useMemo, useEffect } from "react";
import CardStatementPanel from "./CardStatementPanel";
import { CARD_PAYMENT_KEY } from "./scheduleOccurrences";

// ─── constants ────────────────────────────────────────────────────────────────

const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Cap on breakdown lines rendered per cell. Panels are built for every cell in
// the grid (hidden until hover), so the count multiplies by rows × columns ×
// clinics — the cap keeps the DOM bounded. The panel says how many were left off.
const MAX_BREAKDOWN_LINES = 50;

export const ROW_DEFS = [
  { key: "opening", label: "Available Balance", type: "balance" },
  // What NetSuite itself says the account held at the end of each day: the
  // running sum of every posted transaction line up to that date. Sits directly
  // under Available Balance so the two are read together — the projection's
  // starting point beside what is actually posted. It is never fed back into
  // the series, and the row is dropped on cards with no NetSuite account.
  {
    key: "netsuiteTxnBalance",
    label: "NetSuite Transaction Balance",
    sublabel: "posted in NetSuite",
    type: "balance",
  },
  // Reference only — the manually entered end-of-day figure, shown beside the
  // projection but never part of it. `type: "reference"` keeps it out of every
  // balance/net/section code path, and the row is dropped entirely when no
  // closing data exists (see `rowDefs`), so cards without it are unchanged.
  // Closing Available is the expandable parent of the two figures it is computed
  // from, mirroring how Inflows/Outflows expand into their sources. Collapsed by
  // default — it is reference data, and the parent is the summary of the pair.
  {
    key: "closingAvailable",
    label: "Closing Available",
    sublabel: "for reference",
    type: "reference",
    role: "refgroup",
  },
  { key: "closing", label: "Closing Balance", type: "reference", role: "refchild" },
  { key: "closingLoc", label: "Closing LOC Balance", type: "reference", role: "refchild" },
  { key: "inflows", label: "Inflows", type: "section", section: "inflows" },
  { key: "dds", label: "DDS Revenue", type: "child", section: "inflows" },
  { key: "hyg", label: "HYG Revenue", type: "child", section: "inflows" },
  { key: "outflows", label: "Outflows", type: "section", section: "outflows" },
  { key: "associateFee", label: "Associate fees", type: "child", section: "outflows" },
  // NetSuite checks cut by this clinic's subsidiary. Cleared checks sit on their
  // real cleared date; uncleared ones are all stacked on TODAY (see ClinicCashFlow.jsx).
  { key: "checks", label: "Checks", type: "child", section: "outflows" },
  { key: "netCashflow", label: "Net Cashflow", type: "net" },
  { key: "ending", label: "Ending Balance", type: "balance" },
  // The reference counterpart of Ending Balance, rolled the same way:
  //     closing available + net cashflow
  // Reference only, so unlike the real Ending Balance it never carries into the
  // next day — each day stands alone, on the closing figures actually entered.
  {
    key: "closingEnding",
    label: "Closing Ending Balance",
    sublabel: "for reference",
    type: "reference",
  },
];

// ─── helpers ──────────────────────────────────────────────────────────────────

/**
 * Compute the per-day Opening / Ending balance series for one clinic.
 *
 * Opening balance is REAL on any day that has a manually-entered Daily Bank
 * Balance (`realBalanceByDay[day]`) — that actual value is used and resets any
 * drift. On a day without an entry (e.g. future days), the opening is an
 * ESTIMATE equal to the previous day's ending balance (opening + net), so the
 * projection rolls forward. `seedBalance` is the most recent real balance from
 * *before* the visible month, used to anchor the first days until an in-month
 * entry appears; days before any known balance stay null ("—").
 *
 * Returns { opening, openingIsReal, netCashflow, ending } — all `{ date → … }` keyed by ISO date string.
 */
export function computeBalanceSeries({
  days,
  realBalanceByDay = {},
  seedBalance = null,
  inflows = {},
  outflows = {},
  transfers = {},
}) {
  const opening = {};
  const openingIsReal = {};
  const netCashflow = {};
  const ending = {};
  let prevEnding = seedBalance;

  days.forEach(({ date }) => {
    // Intercompany transfers are already signed (money in = +, money out = −),
    // so they add directly to the day's net cash movement.
    const net = (inflows[date] ?? 0) - (outflows[date] ?? 0) + (transfers[date] ?? 0);
    netCashflow[date] = net;

    const real = realBalanceByDay[date];
    let open;
    if (real != null) {
      open = real;
      openingIsReal[date] = true;
    } else if (prevEnding != null) {
      open = prevEnding;
      openingIsReal[date] = false;
    } else {
      open = null;
      openingIsReal[date] = false;
    }
    opening[date] = open;

    const end = open != null ? open + net : null;
    ending[date] = end;
    if (end != null) prevEnding = end;
  });

  return { opening, openingIsReal, netCashflow, ending };
}

export function getDaysInMonth(yearMonth) {
  if (!yearMonth) return [];
  const [year, month] = yearMonth.split("-").map(Number);
  const count = new Date(year, month, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Array.from({ length: count }, (_, i) => {
    const d = i + 1;
    const date = new Date(year, month - 1, d);
    return {
      day: d,
      date: `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      dow: date.getDay(),
      dayName: DAY_NAMES[date.getDay()],
      monthAbbr: MONTH_ABBR[month - 1],
      isExpected: date >= today,
    };
  });
}

/**
 * Generate day objects covering `monthCount` whole months starting from
 * `startMonth`. Each month contributes ALL its days (28/29/30/31) so the last
 * day of a month is never dropped, and the window always ends on a month
 * boundary (e.g. 2 months from July → all of Jul + all of Aug).
 */
export function getSpanMonths(startMonth, monthCount) {
  if (!startMonth || !monthCount) return [];
  const [year, month] = startMonth.split("-").map(Number);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const out = [];
  for (let offset = 0; offset < monthCount; offset++) {
    const m0 = month - 1 + offset; // 0-indexed month, may exceed 11
    const y = year + Math.floor(m0 / 12);
    const m = ((m0 % 12) + 12) % 12;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(y, m, d);
      out.push({
        day: d,
        date: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
        dow: date.getDay(),
        dayName: DAY_NAMES[date.getDay()],
        monthAbbr: MONTH_ABBR[m],
        isExpected: date >= today,
      });
    }
  }
  return out;
}

// "2026-07-24" → "Jul 24", for breakdown lines on multi-day columns. Split
// manually rather than via `new Date(iso)`, which reads a bare date as UTC and
// shifts it a day back for anyone west of Greenwich.
// Why an uncleared payment has not cleared — which SIDE is missing. Three
// answers needing three different actions, so the chip says which rather than
// only that something is outstanding.
const UNCLEARED_REASON = {
  booked_not_cleared: {
    label: "NetSuite only",
    title: "Booked in NetSuite, not yet taken by the bank.",
    className: "bg-palette-indigo-soft text-palette-indigo-text",
  },
  amount_mismatch: {
    label: "Wrong amount",
    title:
      "The bank HAS a payment of this kind on this account in this window — it just could not be paired with this occurrence. Usually another occurrence took it, which means the schedule is split or stale rather than unpaid.",
    className: "bg-palette-amber-muted text-palette-amber-text",
  },
  possible_match: {
    label: "Near match",
    title: "Nothing matched exactly, but something close did — see the figures beside this.",
    className: "bg-palette-gray-subtle text-palette-gray-secondary",
  },
  rejected: {
    label: "Marked not cleared",
    title: "A person reviewed this and recorded that it did NOT clear.",
    className: "bg-palette-rose-soft text-palette-rose-text",
  },
  // Keyed `no_activity` because that is what the API calls it; the CHIP says
  // "Unclear". "No activity" read as a statement about the account — as though
  // nothing at all had happened there — when what it actually means is that
  // nothing could be found for THIS payment, which is a question, not a finding.
  no_activity: {
    label: "Unclear",
    title: "Neither the bank feed nor NetSuite has anything for this payment.",
    className: "bg-palette-rose-soft text-palette-rose-text",
  },
};

// Which side of the reconciliation proved a scheduled payment cleared.
//
// Named after what the two things ARE, not after the endpoints that serve them:
// "NetSuite txn" is a posted GL transaction whose line NetSuite has marked
// cleared (the `ledger-transactions/` side), "Bank feed" is a row the daily
// bank import created (`bank-feed-transactions/`). Both agreeing is the
// strongest evidence available, and the commonest case — the import creates one
// and the booking clears against it.
const CLEARED_SOURCE_LABEL = {
  both: "NetSuite + Bank",
  ledger: "NetSuite txn",
  bank_feed: "Bank feed",
  // Several bank lines added up to one charge — the card centre bills a month's
  // fees as separate MON/VSA/MC/INT/AMX rows. Without this key the chip fell
  // back to the raw source string, so the one match that needed explaining was
  // the one that got none.
  bank_feed_combined: "Bank feed (combined)",
  // A recorded human decision, not an inferred match. Without this key the chip
  // simply did not render for a confirmed payment, so the one state a person had
  // actually vouched for looked LESS certain on screen than the guesses.
  decided: "Confirmed",
};

const CLEARED_SOURCE_TITLE = {
  both: "Both sides agree: a cleared NetSuite posting and the bank-feed row it cleared against.",
  ledger: "A NetSuite posting whose line NetSuite marks cleared.",
  bank_feed: "A bank-feed row only — no cleared NetSuite posting was found.",
  bank_feed_combined:
    "Several same-day bank lines added together to equal one charge, with no cleared NetSuite posting found.",
  decided: "A person confirmed this against the bank. Overrides the automatic matching.",
};

function fmtBreakdownDate(iso) {
  if (!iso) return "";
  const [, m, d] = String(iso).split("-").map(Number);
  return `${MONTH_ABBR[m - 1] ?? ""} ${d}`;
}

/**
 * The reconciliation chips for one payment in a cell's breakdown panel.
 *
 * On their OWN wrapping row, not inline with the label. Inline, three chips
 * pushed the amount far off to the right of a `min-w-max` line and the panel
 * could only be read by scrolling sideways — the detail was there and unusable.
 *
 * The specifics (matched row, the amount and date it matched, what tolerance
 * would clear a near miss) are rendered as TEXT here rather than hidden in a
 * `title`. A native tooltip cannot be scrolled or selected, and a long one gets
 * clipped by the viewport, so anything worth reading does not belong in one.
 * Titles that remain are one short sentence each.
 */
function ReconChips({ item }) {
  const chips = [];

  // By Company: the line is a whole clinic-day, not one payment, so it can hold
  // a mix. Report the split instead of a single state — without this the panel
  // showed the amount and said nothing about whether the money had actually
  // moved, which is the question the cell is usually being hovered to answer.
  // Amounts are in the titles: two counts read at a glance, four figures do not.
  if (item.recon) {
    const { clearedCount, clearedAmount, unclearedCount, unclearedAmount } = item.recon;
    // Uncleared first — it is the exception worth acting on.
    if (unclearedCount) {
      chips.push(
        <span
          key="agg-uncleared"
          title={`${unclearedCount} payment${unclearedCount === 1 ? "" : "s"} totalling ${fmtBalance(unclearedAmount)} have NOT cleared the bank. Includes overdue payments carried onto today and fees not yet cut.`}
          className="rounded bg-palette-amber-muted px-1 text-[9px] font-semibold uppercase tracking-wide text-palette-amber-text"
        >
          {unclearedCount} uncleared
        </span>,
      );
    }
    if (clearedCount) {
      chips.push(
        <span
          key="agg-cleared"
          title={`${clearedCount} payment${clearedCount === 1 ? "" : "s"} totalling ${fmtBalance(clearedAmount)} matched bank activity.`}
          className="rounded bg-palette-emerald-soft px-1 text-[9px] font-semibold uppercase tracking-wide text-palette-emerald-text"
        >
          {clearedCount} cleared
        </span>,
      );
    }
    return chips.length ? (
      <div className="mt-0.5 flex flex-wrap items-center gap-1">{chips}</div>
    ) : null;
  }

  if (item.rolledForwardFrom) {
    chips.push(
      <span
        key="overdue"
        title="Not cleared. Shown on this cell because the money is still owed; the payment's own date is unchanged."
        className="rounded bg-palette-amber-muted px-1 text-[9px] font-semibold uppercase tracking-wide text-palette-amber-text"
      >
        Overdue
      </span>,
    );
  } else if (item.uncleared) {
    chips.push(
      <span
        key="uncleared"
        title="Not cleared yet."
        className="rounded bg-palette-amber-muted px-1 text-[9px] font-semibold uppercase tracking-wide text-palette-amber-text"
      >
        Uncleared
      </span>,
    );
  }

  if (item.cleared) {
    const byMemo = item.matchedBy === "memo";
    // The payment sits on the cell of the day it was DUE, so when the bank took
    // it on a different day that gap is the one thing the cell cannot show by
    // itself. Spelled out here — "due Aug 10 · cleared Aug 12 (2 days later)" —
    // rather than left as a bare date the reader has to compare against the
    // column header themselves.
    const clearingDrift = (() => {
      if (!item.clearedDate || !item.date || item.clearedDate === item.date) return "";
      const days = Math.round(
        (new Date(`${item.clearedDate}T00:00:00`) - new Date(`${item.date}T00:00:00`)) / 86400000,
      );
      const when =
        days > 0
          ? `${days} day${days === 1 ? "" : "s"} later`
          : `${-days} day${days === -1 ? "" : "s"} earlier`;
      return ` Due ${fmtBreakdownDate(item.date)} · cleared ${fmtBreakdownDate(item.clearedDate)} (${when}). The payment stays on its due date here.`;
    })();
    chips.push(
      <span
        key="cleared"
        title={
          (byMemo
            ? `Matched on the MEMO, not the amount: NetSuite and the bank both call this ${item.memoCategory ?? "payment"}. The figures differ — see below — which is expected when one debit covers more than the schedule predicts (a loan instalment billed with its interest, say).`
            : item.clearedSource === "decided"
              ? "A person confirmed this against the bank; it overrides the automatic matching."
              : "Matched to bank activity. Amount-and-date matching, so treat it as strongly suggested.") +
          clearingDrift
        }
        className={`rounded px-1 text-[9px] font-semibold uppercase tracking-wide ${
          // A DECIDED payment is certain regardless of how close the original
          // match was: `confidence` grades a guess, and once a person has
          // vouched for it there is no guess left. Reading "Cleared?" on the one
          // state someone actually confirmed was exactly backwards.
          item.clearedSource === "decided" || item.confidence === "high"
            ? "bg-palette-emerald-soft text-palette-emerald-text"
            : "bg-palette-sky-soft text-palette-sky-text"
        }`}
      >
        {item.clearedSource === "decided" || item.confidence === "high" ? "Cleared" : "Cleared?"}
        {item.clearedDate ? ` ${fmtBreakdownDate(item.clearedDate)}` : ""}
      </span>,
    );
    // A memo match is a different claim from an amount match, so it says so on
    // the line rather than only in a title: the amount beside it does NOT equal
    // the schedule, and a reader who assumes it does would read the gap as an
    // error to chase.
    if (byMemo) {
      chips.push(
        <span
          key="by-memo"
          className="rounded bg-palette-indigo-soft px-1 text-[9px] font-semibold uppercase tracking-wide text-palette-indigo-text"
          title={item.matchMemo ? `Memo: ${item.matchMemo}` : undefined}
        >
          via memo{item.memoCategory ? ` · ${item.memoCategory}` : ""}
        </span>,
      );
      if (item.matchAmount != null) {
        chips.push(
          <span key="by-memo-amount" className="text-[9px] text-palette-gray-placeholder">
            bank {fmtBalance(Number(item.matchAmount))}
            {item.amountDelta ? ` · ${fmtBalance(Number(item.amountDelta))} over schedule` : ""}
          </span>,
        );
      }
    }
    if (CLEARED_SOURCE_LABEL[item.clearedSource]) {
      const d = item.decision;
      chips.push(
        <span
          key="src"
          title={
            (CLEARED_SOURCE_TITLE[item.clearedSource] ?? "") +
            (d?.confirmed_by ? `\n\nConfirmed by ${d.confirmed_by}` : "") +
            (d?.confirmed_at ? ` on ${String(d.confirmed_at).slice(0, 10)}` : "") +
            (d?.matched_reference ? `\nAgainst ${d.matched_reference}` : "")
          }
          className={`rounded px-1 text-[9px] font-semibold uppercase tracking-wide ${
            item.clearedSource === "decided"
              ? // A person vouched for this, so it reads as settled rather than
                // as one more piece of inferred evidence.
                "bg-palette-emerald-badge text-white"
              : "border border-palette-gray-divider bg-white text-palette-gray-secondary"
          }`}
        >
          {CLEARED_SOURCE_LABEL[item.clearedSource]}
        </span>,
      );
    }
    if (item.matchReference) {
      chips.push(
        item.matchLink ? (
          // stopPropagation: the cell underneath is clickable (move a payment),
          // and opening NetSuite must not also trigger it.
          <a
            key="ref"
            href={item.matchLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Open the matched transaction in NetSuite"
            className="text-[9px] text-palette-indigo-heading underline decoration-dotted underline-offset-2 hover:decoration-solid"
          >
            {item.matchReference}
            {item.matchAmount ? ` · $${item.matchAmount}` : ""}
          </a>
        ) : (
          <span key="ref" className="text-[9px] text-palette-gray-placeholder">
            {item.matchReference}
            {item.matchAmount ? ` · $${item.matchAmount}` : ""}
          </span>
        ),
      );
    }
  } else if (UNCLEARED_REASON[item.unclearedReason]) {
    const reason = UNCLEARED_REASON[item.unclearedReason];
    chips.push(
      <span
        key="reason"
        title={reason.title}
        className={`rounded px-1 text-[9px] font-semibold uppercase tracking-wide ${reason.className}`}
      >
        {reason.label}
      </span>,
    );
    if (item.bookedNotCleared) {
      chips.push(
        <span key="booked" className="text-[9px] text-palette-gray-placeholder">
          {item.bookedNotCleared.reference} · ${item.bookedNotCleared.amount}
        </span>,
      );
    }
    // The near miss, spelled out: which row, how far off, and the tolerance
    // that would clear it. This is the number an operator tunes with, so it is
    // the last thing that should be buried in a tooltip.
    // What `amount_mismatch` is asserting, as text: the row the bank does hold.
    // The chip alone would say "wrong amount" without saying wrong against
    // WHAT, which is the actionable half.
    if (item.categoryEvidence) {
      const ev = item.categoryEvidence;
      chips.push(
        <span key="evidence" className="text-[9px] text-palette-gray-placeholder">
          bank has {fmtBalance(Number(ev.amount))} on {fmtBreakdownDate(ev.date)}
          {ev.memo ? ` · ${ev.memo}` : ""}
        </span>,
      );
    }

    const best = item.suggestions?.[0];
    if (best) {
      chips.push(
        <span key="sugg" className="text-[9px] text-palette-gray-placeholder">
          near {best.reference} ${best.amount} · off ${best.amount_delta}/{best.day_delta}d · clears
          at {best.clears_at_tolerance}
        </span>,
      );
    }
  }

  if (!chips.length) return null;
  return <div className="mt-0.5 flex flex-wrap items-center gap-1">{chips}</div>;
}

/**
 * Flatten a row's `{ date → [{label, amount}] }` breakdown into the lines a
 * hovered cell shows, across every day that cell covers.
 *
 * NOTHING IS COMBINED: one record in, one line out. Summing same-label records
 * into a single line read as tidier and hid the only thing the panel exists to
 * show. Two payments of the same amount to the same payee on the same day are
 * two things a person has to account for separately, and each carries its OWN
 * reconciliation detail — three uncleared loan occurrences carried onto today
 * appeared as one $23,427 line with one set of chips, when each had a different
 * due date, a different piece of evidence and a different reason for not
 * clearing.
 *
 * Every line keeps the `date` of the day it came from, so a column covering
 * more than one day can say which day each record belongs to.
 */
function collectBreakdown(byDate, colDays) {
  if (!byDate) return [];
  const lines = [];
  colDays.forEach(({ date }) => {
    (byDate[date] ?? []).forEach((item) => {
      lines.push({ ...item, date, amount: Number(item.amount) || 0 });
    });
  });
  return lines;
}

/**
 * Generate `dayCount` consecutive day objects starting at `startISO`.
 *
 * The rolling-window counterpart to `getSpanMonths`: the cash-flow grids show a
 * fixed 30/60-day span from a chosen start date rather than whole calendar
 * months, so the window is not anchored to the 1st and freely crosses month
 * (and year) boundaries. Same day shape as `getSpanMonths`, so every consumer
 * downstream is indifferent to which one produced it.
 */
export function getDayWindow(startISO, dayCount) {
  if (!startISO || !dayCount) return [];
  const [y, m, d] = startISO.split("-").map(Number);
  if (!y || !m || !d) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const out = [];
  for (let i = 0; i < dayCount; i++) {
    // Constructing from (y, m-1, d + i) lets Date normalise the overflow, so a
    // window starting Dec 20 rolls into January without special-casing.
    const date = new Date(y, m - 1, d + i);
    out.push({
      day: date.getDate(),
      date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
        date.getDate(),
      ).padStart(2, "0")}`,
      dow: date.getDay(),
      dayName: DAY_NAMES[date.getDay()],
      monthAbbr: MONTH_ABBR[date.getMonth()],
      isExpected: date >= today,
    });
  }
  return out;
}

/** Shift an ISO date by `n` days, returning ISO. */
export function shiftISO(iso, n) {
  if (!iso) return iso;
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d + n);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

// Group an array of day objects (from getDaysInMonth) into weekly buckets
// that start on Monday. Returns an array of column descriptors.
// Exported so EntityCashFlowTable's weekly view buckets identically — a second
// implementation would drift on the week-boundary rules.
export function groupDaysIntoWeeks(days) {
  const weeks = [];
  let currentWeek = null;
  days.forEach((d) => {
    if (!currentWeek || d.dow === 1) {
      // Find the Monday that anchors this week.
      const [y, m, day] = d.date.split("-").map(Number);
      const daysBack = (d.dow + 6) % 7; // 0 if already Monday
      const mondayDate = new Date(y, m - 1, day - daysBack);
      const mondayLabel = `${MONTH_ABBR[mondayDate.getMonth()]} ${mondayDate.getDate()}`;
      currentWeek = {
        key: `wk-${d.date}`,
        header1: "WK OF",
        header2: mondayLabel,
        days: [],
        isExpected: false,
      };
      weeks.push(currentWeek);
    }
    currentWeek.days.push(d);
    if (d.isExpected) currentWeek.isExpected = true;
  });
  return weeks;
}

// Compute the display value for one column (daily = single day, weekly = week,
// total = all days). Balance rows use the first/last day of the column;
// everything else is summed across all days in the column.
// All data maps are keyed by ISO date string.
function getCellValue(rowKey, rowType, colDays, enrichedData) {
  if (rowType === "balance") {
    if (rowKey === "opening") return enrichedData.opening?.[colDays[0]?.date] ?? null;
    if (rowKey === "ending")
      return enrichedData.ending?.[colDays[colDays.length - 1]?.date] ?? null;
    // A running balance, so a column reports where the account stood at its END
    // rather than a sum across days — but nothing is posted past today, so the
    // series simply stops there. Walk back to the last day in the column that
    // actually carries a figure: a week or Period Total straddling today then
    // reports today's balance instead of going blank because the column happens
    // to end in the future. A column entirely in the future has nothing to walk
    // back to and correctly stays empty.
    if (rowKey === "netsuiteTxnBalance") {
      for (let i = colDays.length - 1; i >= 0; i -= 1) {
        const hit = enrichedData.netsuiteTxnBalance?.[colDays[i]?.date];
        if (hit != null) return hit;
      }
      return null;
    }
  }
  // Reference and credit-card rows hold {amount, time} per day, so they can't be
  // summed across a column. Report the LAST day in range that actually has a
  // figure: in a weekly or total column the most recent close is the meaningful
  // one, and falling back through the column stops a blank Friday from hiding
  // Thursday's entry. The two types differ only in how they RENDER — a card
  // block is a full-size block of its own, not an annotation on the row above.
  if (rowType === "reference" || rowType === "card") {
    for (let i = colDays.length - 1; i >= 0; i -= 1) {
      const hit = enrichedData[rowKey]?.[colDays[i]?.date];
      if (hit) return hit;
    }
    return null;
  }
  if (rowType === "net") {
    return colDays.reduce((s, d) => s + (enrichedData.netCashflow?.[d.date] ?? 0), 0);
  }
  let total = 0,
    hasAny = false;
  for (const d of colDays) {
    const v = enrichedData[rowKey]?.[d.date];
    if (v != null) {
      total += v;
      hasAny = true;
    }
  }
  return hasAny ? total : null;
}

function fmtBalance(value) {
  if (value == null) return "—";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs >= 1000) return `${sign}$${Math.round(abs).toLocaleString("en-CA")}`;
  return `${sign}$${abs.toLocaleString("en-CA")}`;
}

// Clock time for the closing-balance stamp ("4:45 PM"). Invalid or missing
// timestamps render as nothing rather than "Invalid Date" — the field is a
// schemaless JSON blob, so the value is not guaranteed to be a real date.
export function fmtClockTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
}

function fmtNet(value) {
  if (value == null || value === 0) return "$0";
  const abs = Math.round(Math.abs(value)).toLocaleString("en-CA");
  return value > 0 ? `+$${abs}` : `-$${abs}`;
}

// Small colored pill for a transfer's approval status.
const TRANSFER_STATUS_STYLES = {
  approved: "bg-palette-emerald-soft text-smiles-green",
  pending: "bg-palette-amber-soft text-palette-amber-badge",
  rejected: "bg-palette-rose-soft text-smiles-redOrange",
};

function TransferStatusPill({ status }) {
  if (!status) return null;
  const cls =
    TRANSFER_STATUS_STYLES[status] ?? "bg-palette-gray-subtle text-palette-gray-secondary";
  return (
    <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${cls}`}>
      {status}
    </span>
  );
}

/**
 * The due date on a "Statement balance" row, as a chip beside the label.
 *
 * A due date is a DEADLINE, not a daily figure, so it rides the row label
 * instead of taking a column — the cells already say what is owed on each day,
 * and when it has to be paid is the one thing they cannot say.
 *
 * Deliberately a different shape from `TransferStatusPill` above: squared
 * corners, a border, and letter-spaced small caps. That pill reports a STATE
 * that changes as a transfer progresses; this is a fixed term of the statement,
 * and giving the two the same shape would invite reading this one as a status.
 * `cursor-default` for the same reason — nothing here is clickable.
 */
function DueDatePill({ text }) {
  if (!text) return null;
  return (
    <span className="ml-2 cursor-default whitespace-nowrap rounded border border-palette-emerald-border bg-palette-emerald-soft px-1.5 py-px text-[10.5px] font-semibold uppercase tracking-[0.04em] text-palette-emerald-text">
      {text}
    </span>
  );
}

function clinicInitials(label) {
  const name = label.replace(/\s*\(.*\)$/, "").trim();
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ─── shared primitives ────────────────────────────────────────────────────────

function Chevron({ open, className = "w-3.5 h-3.5" }) {
  return (
    <svg
      className={`${className} transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
      viewBox="0 0 10 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SummaryCell({ label, value, colorClass }) {
  return (
    <div className="text-right">
      <div className="text-xs font-semibold tracking-widest text-palette-gray-secondary uppercase">
        {label}
      </div>
      <div className={`text-base font-bold ${colorClass}`}>{value}</div>
    </div>
  );
}

function CellValue({
  value,
  rowType,
  section,
  estimate = false,
  muted = false,
  // Money committed but not yet taken by the bank (uncleared checks). Rendered
  // amber rather than the outflow red: it IS deducted from the projection, but
  // the date it lands on is a guess, not a fact.
  uncleared = false,
}) {
  // Reference row: `value` is {amount, time}, not a number. Rendered lighter
  // than the projection rows above it, with the capture time beneath, so it
  // never reads as a figure the forecast depends on.
  if (rowType === "reference") {
    if (value == null || value.amount == null) {
      return <span className="text-palette-gray-placeholder">—</span>;
    }
    return (
      <span className="inline-block leading-tight">
        <span className="font-normal text-palette-gray-placeholder">
          {fmtBalance(value.amount)}
        </span>
        {value.time && (
          <span className="block text-[10px] font-normal text-palette-gray-border">
            {fmtClockTime(value.time)}
          </span>
        )}
      </span>
    );
  }

  // Credit card block: the same {amount, time} shape as a reference row, but
  // rendered at the weight of a real line of the statement — the block is read
  // on its own, not as a footnote to the balance above it.
  if (rowType === "card") {
    if (value == null || value.amount == null) {
      return <span className="text-palette-gray-placeholder">—</span>;
    }
    // The day a statement's last payment lands carries a WORD, not a figure:
    // "$0 owed" and "paid" are the same fact, and the word is the one people
    // are looking for. It replaces the amount rather than sitting under it —
    // a zero beside it would only invite reading the cell as an amount again.
    if (value.note) {
      return <span className="font-medium text-smiles-green">{value.note}</span>;
    }
    // A day a payment landed on is green, so the days money actually moved can
    // be picked out of a row of otherwise identical balances. The figure is
    // still what is OWED after that payment — the colour marks the event, the
    // number reports the position.
    const tookPayment = value.paidToday > 0;
    return (
      <span className="inline-block leading-tight">
        <span
          className={`font-medium ${tookPayment ? "text-smiles-green" : "text-palette-gray-primary"}`}
          title={tookPayment ? `${fmtBalance(value.paidToday)} paid this day` : undefined}
        >
          {fmtBalance(value.amount)}
        </span>
        {value.time && (
          <span className="block text-[10px] font-normal text-palette-gray-placeholder">
            {fmtClockTime(value.time)}
          </span>
        )}
      </span>
    );
  }

  if (rowType === "net") {
    if (value == null || value === 0) {
      return <span className="text-palette-gray-placeholder font-semibold">$0</span>;
    }
    const positive = value > 0;
    return (
      <span
        className={`inline-block rounded px-1 font-bold text-xs ${positive ? "text-smiles-green bg-palette-emerald-soft" : "text-smiles-redOrange bg-palette-rose-soft"}`}
      >
        {fmtNet(value)}
      </span>
    );
  }

  if (rowType === "balance") {
    if (value == null) return <span className="text-palette-gray-placeholder">—</span>;
    // Negative balances always render red, overriding the estimate/normal color.
    // Estimated opening balances (carried from the prior day's ending) render
    // italic with a "~" so they're visually distinct from real, entered values.
    const color =
      value < 0
        ? "text-smiles-redOrange"
        : estimate
          ? "text-palette-indigo-badge"
          : "text-palette-gray-primary";
    return (
      <span className={`${estimate ? "italic" : "font-medium"} ${color}`}>
        {estimate ? "~" : ""}
        {fmtBalance(value)}
      </span>
    );
  }

  if (value == null || value === 0) {
    return <span className="text-palette-gray-placeholder">—</span>;
  }

  if (rowType === "section" || rowType === "child") {
    // Transfers are signed per direction: negative = money out (deducted),
    // positive = money in (added). Everything else is fixed by section.
    const negative = section === "transfers" ? value < 0 : section === "outflows";
    const abs = Math.abs(value);
    const text =
      section === "transfers"
        ? `${negative ? "-" : "+"}${fmtBalance(abs)}`
        : negative
          ? `-${fmtBalance(abs)}`
          : fmtBalance(value);
    // Muted (e.g. expected-but-not-yet-cleared associate fees): shown for
    // reference in light gray, since they don't affect the balance yet.
    if (muted) {
      return <span className="text-palette-gray-placeholder font-light italic">{text}</span>;
    }
    // Uncleared checks: amber and badged, so it never reads as settled money
    // the way the red outflow figure does.
    if (uncleared) {
      return (
        <span className="inline-block rounded bg-palette-amber-muted px-1 text-palette-amber-text">
          {text}
        </span>
      );
    }
    // Text color marks inflow (green) vs. outflow (red), matching the cell
    // background (see getCellBgClass).
    return <span className={negative ? "text-smiles-redOrange" : "text-smiles-green"}>{text}</span>;
  }

  return <span className="text-palette-gray-primary">{fmtBalance(value)}</span>;
}

// Background used on section/child cells: amounts are tinted green/red by
// direction. Past and expected columns are shaded the same way — the historical
// region used to get a flat neutral band, but that muted real actuals and made
// the grid read as two disconnected halves.
function getCellBgClass(value, rowType, section) {
  if (rowType !== "section" && rowType !== "child") return "";
  if (value == null || value === 0) return "";
  if (section === "transfers") {
    return value < 0 ? "bg-palette-rose-soft" : "bg-palette-emerald-soft";
  }
  return section === "outflows" ? "bg-palette-rose-soft" : "bg-palette-emerald-soft";
}

// ─── main component ───────────────────────────────────────────────────────────

export default function ClinicCashFlowCard({
  code,
  label,
  // The clinic's cash accounts (labels), listed under the code in the header.
  bankNames = [],
  // date → running end-of-day NetSuite GL balance for the clinic's account(s).
  netsuiteBalanceByDay = {},
  // Whether this card's account exists in NetSuite at all. Kept separate from
  // the data above so the row's presence depends on the ACCOUNT, not on the
  // request having landed: an in-flight or failed lookup shows a row of dashes,
  // which is diagnosable, where a vanishing row reads as "not built yet".
  hasNetsuiteAccount = false,
  days,
  open,
  onToggle,
  realBalanceByDay = {},
  seedBalance = null,
  // date → { amount, time } for the manually entered closing balance. Reference
  // data: absent for the aggregate (By Company) card, which has no single
  // clinic's entry times to report.
  closingByDay = {},
  closingLocByDay = {},
  closingAvailableByDay = {},
  // Clinic-owned credit cards, one expandable block each below Closing Ending
  // Balance: [{ key, label, creditLimit, balanceByDay, availableByDay,
  // statementByDay, statementDue }], where
  // each *ByDay is `date → {amount, time}` like the closing maps beside them.
  // Reference data — a card balance is money owed, and nothing here feeds the
  // cash projection.
  creditCards = [],
  // One statement-detail panel per credit card, rendered under the table (see
  // CardStatementPanel). Keyed by the card's row key so a card's rows and its
  // panel cannot drift apart. Empty for By Company, like `creditCards` itself.
  cardDetails = {},
  // Raised by a panel line: a charge asks for its SCHEDULE to be edited, a
  // payment (or the statement action) for its STATEMENT. Both are owned by the
  // page — the same modals the Monthly Inputs and All Payments tabs use — and
  // both are omitted for a viewer who may not edit, which is what removes the
  // controls rather than disabling them.
  onEditCardCharge,
  onEditCardStatement,
  data = {},
  extraInflowRows = [],
  extraOutflowRows = [],
  // Nested "Intercompany Transfer" groups for the SFC card: line items rendered
  // as an expandable group under the Inflows / Outflows sections respectively.
  inflowTransferItems = [],
  outflowTransferItems = [],
  // Intercompany transfer line items for this clinic: [{ key, label }]. Their
  // per-day signed amounts live in `data[key]`; the section net is `data.transfers`.
  transferItems = [],
  occurrences = {},
  breakdowns = {},
  // What one breakdown line means: "payments" (By Clinic — the individual
  // payments inside a cell) or "clinics" (By Company — each clinic's share of
  // the aggregate). Only affects how lines are merged, ordered and labelled;
  // both read the same `breakdowns` shape.
  breakdownMode = "payments",
  onCellClick,
  // Credit card payment cells. That row has no SchedulePayment behind it, so
  // `onCellClick`'s Move-occurrence modal cannot serve it; this opens the
  // STATEMENT instead. Called with { statementId, cardAccountId }.
  onCardPaymentClick,
  onExpectedFeeClick,
  viewMode = "daily",
  // When provided (By Company view), the Opening/Ending/Net series is already
  // aggregated across clinics and passed in directly, instead of being derived
  // from a single clinic's real balances via computeBalanceSeries.
  precomputedSeries = null,
  // When true, hides the DDS/HYG revenue child rows under Inflows (e.g. SFC
  // which has no production revenue — only intercompany transfers).
  hideInflowRows = false,
  // When true, hides the Checks row. The check search is per clinic subsidiary,
  // so a card that isn't one clinic's subsidiary (SFC) has nothing to show.
  hideChecksRow = false,
  // Page-level "Download CSV": called with (code, build) where build() returns
  // this card's rows as CSV records; returns the unregister function.
  onRegisterCsvExport,
}) {
  const byClinicBreakdown = breakdownMode === "clinics";
  const [inflowsOpen, setInflowsOpen] = useState(true);
  const [outflowsOpen, setOutflowsOpen] = useState(true);
  const [transfersOpen, setTransfersOpen] = useState(false);
  // Reference block (Closing Available → its two components). Collapsed by
  // default: the parent already summarises the pair, and this is reference data
  // that should not crowd the projection rows.
  const [closingOpen, setClosingOpen] = useState(false);
  // Expand state per credit card block, keyed by the card's row key.
  //
  // EXPANDED by default, which is why the map holds only what has been toggled
  // and an absent key reads as open (`isCardOpen`). A card's rows and the panel
  // below them are the reason the block exists; hiding them behind a chevron
  // meant the balance, the headroom and the statement all had to be asked for.
  const [cardsOpen, setCardsOpen] = useState({});
  const isCardOpen = (key) => cardsOpen[key] ?? true;
  // Expand state for nested "Intercompany Transfer" groups (keyed by groupId,
  // e.g. inflowsXfer / outflowsXfer). Collapsed by default.
  const [xferGroupsOpen, setXferGroupsOpen] = useState({});

  // Breakdown panel PINNED open by clicking a cell (`${rowKey}|${colKey}`, one
  // at a time). Hover alone closes the panel the moment the pointer leaves the
  // cell, which makes it impossible to reach a link inside it or read a long
  // list — a pinned panel stays until it's dismissed.
  const [pinnedCell, setPinnedCell] = useState(null);

  // Dismiss the pinned panel on Escape, or on a click anywhere that isn't the
  // panel itself (the cell's own onClick handles re-clicking the same cell).
  useEffect(() => {
    if (!pinnedCell) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPinnedCell(null);
    };
    const onDown = (e) => {
      if (e.target.closest?.("[data-breakdown-panel]")) return;
      if (e.target.closest?.("[data-breakdown-cell]")) return;
      setPinnedCell(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [pinnedCell]);

  // Insert dynamic rows just before the Net Cashflow row: extra outflow rows
  // (expense types without a predefined row) and, when this clinic has any
  // intercompany transfers, an "Intercompany Transfer" section + one child per
  // transfer line item.
  // Any reference figure at all on this card — decides whether the whole
  // Closing block earns a place (see `rowDefs`).
  const hasAnyClosing =
    Object.keys(closingAvailableByDay).length > 0 ||
    Object.keys(closingByDay).length > 0 ||
    Object.keys(closingLocByDay).length > 0;

  const rowDefs = useMemo(() => {
    const INFLOW_CHILD_KEYS = new Set(["dds", "hyg"]);
    const hidden = new Set([
      ...(hideInflowRows ? INFLOW_CHILD_KEYS : []),
      ...(hideChecksRow ? ["checks"] : []),
      // Reference block. Nothing on file (or an aggregate card, which is never
      // given any) drops all three rather than printing lines of dashes.
      // Otherwise the parent always stays — it owns the expand toggle, so
      // hiding it would strand the children even when they have data — while
      // each child appears only when expanded AND it has something to show.
      // Closing Ending Balance is derived from Closing Available, so it lives or
      // dies with it. It sits at the bottom by Ending Balance, outside the
      // collapsible block, so the expand state does not apply.
      ...(Object.keys(closingAvailableByDay).length === 0 ? ["closingEnding"] : []),
      // Only cards with no NetSuite account behind them at all drop the row —
      // aggregates, and manually-added accounts with no NetSuite id.
      ...(hasNetsuiteAccount ? [] : ["netsuiteTxnBalance"]),
      ...(!hasAnyClosing
        ? ["closingAvailable", "closing", "closingLoc"]
        : [
            ...(closingOpen && Object.keys(closingByDay).length > 0 ? [] : ["closing"]),
            ...(closingOpen && Object.keys(closingLocByDay).length > 0 ? [] : ["closingLoc"]),
          ]),
    ]);
    const base = hidden.size ? ROW_DEFS.filter((d) => !hidden.has(d.key)) : ROW_DEFS;
    // Plain dynamic inflow/outflow child rows (e.g. rent, loan, interest).
    const inflowExtras = extraInflowRows.map((r) => ({
      key: r.key,
      label: r.label,
      status: r.status,
      type: "child",
      section: "inflows",
    }));
    const outflowExtras = extraOutflowRows.map((r) => ({
      key: r.key,
      label: r.label,
      type: "child",
      section: "outflows",
    }));
    // Nested "Intercompany Transfer" group: one expandable row per section, whose
    // children are the individual transfer lines (From/To clinic + status).
    const buildXferGroup = (items, section, groupKey) =>
      items.length
        ? [
            {
              key: groupKey,
              label: "Intercompany Transfer",
              type: "child",
              section,
              role: "group",
              groupId: groupKey,
            },
            ...items.map((r) => ({
              key: r.key,
              label: r.label,
              status: r.status,
              type: "child",
              section,
              role: "groupchild",
              groupId: groupKey,
            })),
          ]
        : [];
    // One block per credit card, appended after the whole base list — i.e. below
    // Closing Ending Balance, at the very bottom of the card. The parent row is
    // labelled with the card and carries the Balance figure, so a collapsed
    // block still answers "what is owed on this card"; expanding it names that
    // figure and adds the headroom it implies.
    //
    // `type: "card"` rather than `"reference"`: the VALUES behave the same way
    // (point-in-time {amount, time}, see getCellValue), but a card block is its
    // own section of the statement and is set at the weight of Inflows /
    // Outflows — not in the faint annotation style of the closing rows, which
    // exist to be read against the balance directly above them.
    const cardRows = creditCards.flatMap((c) => {
      // Whether a statement actually covers the window. It no longer decides
      // whether the ROW exists — that is now unconditional — only whether there
      // is a due date to chip onto it.
      const hasStatement = !!c.statementDue && Object.keys(c.statementByDay ?? {}).length > 0;
      return [
        {
          key: c.key,
          label: c.label,
          sublabel: "credit card",
          // Repeated on the header so a COLLAPSED block still answers "when is
          // this card due" — the whole point of collapsing it is to stop reading
          // its rows.
          badge: hasStatement ? c.statementDue : null,
          type: "card",
          role: "cardgroup",
          cardId: c.key,
        },
        ...((cardsOpen[c.key] ?? true)
          ? [
              {
                key: `${c.key}Balance`,
                label: "Balance",
                type: "card",
                role: "cardchild",
                cardId: c.key,
              },
              {
                key: `${c.key}Available`,
                label: "Available",
                // Spelled out because the word alone is ambiguous on a card: a
                // bank account's "available" is cash on hand, a card's is
                // headroom left to spend. Sublabel rather than a longer label,
                // so it stays inside the 200px Line Item column.
                sublabel: "limit − balance",
                type: "card",
                role: "cardchild",
                cardId: c.key,
              },
              // Statement balance — the entered statement from Monthly Inputs,
              // with its due date chipped onto the label. Third and last, under
              // the two figures read off the daily balance entry: Balance and
              // Available are what the card stands at TODAY, this is what a
              // statement says must be cleared and by when.
              //
              // ALWAYS present, even with no statement in the window. A row of
              // dashes says "nothing has been entered for this card yet", which
              // is a fact worth seeing; a row that disappears just leaves the
              // block looking complete when a statement is actually missing.
              {
                key: `${c.key}Statement`,
                label: "Statement balance",
                // Rendered as a chip after the label, not appended to it — see
                // DueDatePill. Null when nothing is outstanding, since there is
                // no date to name.
                badge: hasStatement ? c.statementDue : null,
                type: "card",
                role: "cardchild",
                cardId: c.key,
              },
            ]
          : []),
      ];
    });
    const inflowGroup = buildXferGroup(inflowTransferItems, "inflows", "inflowsXfer");
    const outflowGroup = buildXferGroup(outflowTransferItems, "outflows", "outflowsXfer");
    const transferRows = transferItems.length
      ? [
          {
            key: "transfers",
            label: "Intercompany Transfer",
            type: "section",
            section: "transfers",
          },
          ...transferItems.map((t) => ({
            key: t.key,
            label: t.label,
            status: t.status,
            type: "child",
            section: "transfers",
          })),
        ]
      : [];
    if (
      !inflowExtras.length &&
      !outflowExtras.length &&
      !inflowGroup.length &&
      !outflowGroup.length &&
      !transferRows.length &&
      !cardRows.length
    )
      return base;
    // Inflow extras + inflow transfer group land at the end of the Inflows
    // section (just before Outflows); outflow extras + outflow transfer group +
    // any legacy transfer section go just before Net Cashflow.
    const outflowIdx = base.findIndex((d) => d.key === "outflows");
    const netIdx = base.findIndex((d) => d.key === "netCashflow");
    return [
      ...base.slice(0, outflowIdx),
      ...inflowExtras,
      ...inflowGroup,
      ...base.slice(outflowIdx, netIdx),
      ...outflowExtras,
      ...outflowGroup,
      ...transferRows,
      ...base.slice(netIdx),
      ...cardRows,
    ];
  }, [
    extraInflowRows,
    extraOutflowRows,
    inflowTransferItems,
    outflowTransferItems,
    transferItems,
    hideInflowRows,
    hideChecksRow,
    closingByDay,
    closingLocByDay,
    closingAvailableByDay,
    hasNetsuiteAccount,
    hasAnyClosing,
    closingOpen,
    creditCards,
    cardsOpen,
  ]);

  // Opening (real entry, else previous day's ending), Net Cashflow, and
  // Ending balance per day — see computeBalanceSeries.
  const enrichedData = useMemo(() => {
    const series =
      precomputedSeries ??
      computeBalanceSeries({
        days,
        realBalanceByDay,
        seedBalance,
        inflows: data?.inflows ?? {},
        outflows: data?.outflows ?? {},
        transfers: data?.transfers ?? {},
      });
    // `closing` / `closingLoc` hold {amount, time} objects, not numbers — only
    // the reference rows read them, keyed by row key, and they deliberately
    // bypass computeBalanceSeries.
    // Closing Ending Balance: the reference mirror of `ending`, rolled the same
    // way — closing available + that day's net cashflow. Built here rather than
    // in ClinicCashFlow.jsx because the net series lives in `computeBalanceSeries`.
    //
    // It deliberately does NOT chain: the real ending carries into the next
    // day's opening, but this one is anchored to entered closing figures, so a
    // day with no entry gets no row value rather than a rolled-forward estimate.
    // No `time`: the amount is part entered figure, part projected net, so a
    // capture stamp would describe only half of it. CellValue omits the second
    // line when there is no time.
    const closingEnding = {};
    Object.entries(closingAvailableByDay).forEach(([date, ref]) => {
      closingEnding[date] = { amount: ref.amount + (series.netCashflow?.[date] ?? 0) };
    });

    // Credit card blocks: one entry per row key the card block declares. The
    // parent shows the same Balance series as its first child — it is the
    // block's summary, the way Closing Available summarises its pair — so a
    // collapsed block is not a row of dashes.
    const cardSeries = {};
    creditCards.forEach((c) => {
      cardSeries[c.key] = c.balanceByDay ?? {};
      cardSeries[`${c.key}Balance`] = c.balanceByDay ?? {};
      cardSeries[`${c.key}Available`] = c.availableByDay ?? {};
      cardSeries[`${c.key}Statement`] = c.statementByDay ?? {};
    });

    return {
      ...data,
      ...series,
      closing: closingByDay,
      closingLoc: closingLocByDay,
      closingAvailable: closingAvailableByDay,
      closingEnding,
      ...cardSeries,
      // Read straight through: NetSuite's own end-of-day figure is an
      // observation, not part of the projection, so it is deliberately NOT fed
      // into computeBalanceSeries the way realBalanceByDay is.
      netsuiteTxnBalance: netsuiteBalanceByDay,
    };
  }, [
    data,
    realBalanceByDay,
    seedBalance,
    days,
    precomputedSeries,
    closingByDay,
    closingLocByDay,
    closingAvailableByDay,
    netsuiteBalanceByDay,
    creditCards,
  ]);

  // Columns to render — one per day in daily mode, one per week in weekly mode.
  // The Period Total column is always appended separately.
  //
  // Every column renders in full. Past days used to fold into a single "Past"
  // column, which mattered when the window was a calendar month starting on the
  // 1st; the window now starts at today, so there is usually nothing behind it
  // to fold.
  const columns = useMemo(() => {
    const cols =
      viewMode === "weekly"
        ? groupDaysIntoWeeks(days)
        : days.map((d) => ({
            key: `d-${d.date}`,
            header1: d.dayName,
            header2: `${d.monthAbbr} ${d.day}`,
            days: [d],
            isExpected: d.isExpected,
          }));
    // Past columns render on a gray wash so settled history is never read as a
    // projection. `isExpected` is already "date >= today", so past is its
    // negation — and a WEEK is past only when none of its days remain, since
    // groupDaysIntoWeeks marks a week expected if any single day is.
    // `isPastEdge` is the last past column, which carries the today divider.
    return cols.map((c, i) => ({
      ...c,
      isPast: !c.isExpected,
      isPastEdge: !c.isExpected && !!cols[i + 1]?.isExpected,
    }));
  }, [days, viewMode]);

  // CSV rows for the page's "Download CSV": the same columns the grid shows.
  // Collapsed Inflows / Outflows / transfer sections are still exported — a
  // download is for the figures, not for the expand state the card was left in.
  useEffect(() => {
    if (!onRegisterCsvExport) return undefined;
    const toNumber = (v) => {
      const n = v != null && typeof v === "object" ? v.amount : v;
      return n == null || !Number.isFinite(Number(n)) ? "" : Math.round(Number(n) * 100) / 100;
    };
    const cardLabelById = Object.fromEntries(creditCards.map((c) => [c.key, c.label]));
    const build = () =>
      rowDefs.map((def) => {
        let lineItem = def.label;
        if (def.role === "cardchild") lineItem = `${cardLabelById[def.cardId]} - ${def.label}`;
        if (def.status) lineItem = `${lineItem} (${def.status})`;
        const row = { Clinic: label, "Line Item": lineItem };
        columns.forEach((col) => {
          const header = viewMode === "weekly" ? `Week of ${col.header2}` : col.days[0].date;
          row[header] = toNumber(getCellValue(def.key, def.type, col.days, enrichedData));
        });
        row["Period Total"] = toNumber(getCellValue(def.key, def.type, days, enrichedData));
        return row;
      });
    return onRegisterCsvExport(code, build);
  }, [
    onRegisterCsvExport,
    code,
    label,
    rowDefs,
    columns,
    days,
    enrichedData,
    creditCards,
    viewMode,
  ]);

  const clinicName = label.replace(/\s*\(.*\)$/, "").trim();
  const initials = clinicInitials(label);
  const lastDate = days.length ? days[days.length - 1].date : null;
  const netTotal = Object.keys(enrichedData.netCashflow ?? {}).length
    ? Object.values(enrichedData.netCashflow).reduce((sum, v) => sum + (v ?? 0), 0)
    : null;
  const endBalance = lastDate != null ? (enrichedData.ending[lastDate] ?? null) : null;

  return (
    <div className="rounded-xl border border-palette-gray-divider bg-white shadow-sm overflow-hidden">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-palette-gray-divider">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggle}
            className="flex items-center justify-center w-6 h-6 rounded hover:bg-palette-gray-subtle text-palette-gray-secondary transition-colors"
            aria-label={open ? "Collapse" : "Expand"}
          >
            <Chevron open={open} className="w-3.5 h-3.5" />
          </button>

          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-palette-indigo-muted text-xs font-bold text-palette-indigo-badge shrink-0 select-none">
            {initials}
          </div>

          <div className="leading-tight">
            <div className="font-semibold text-base text-palette-gray-primary">{clinicName}</div>
            <div className="text-sm text-palette-gray-secondary">{code}</div>
            {bankNames.map((name) => (
              <div key={name} className="text-xs text-palette-gray-secondary">
                {/* "Bank Balance : RBC: Bells Corner" -> "RBC: Bells Corner" */}
                <span title={name}>{name.replace(/^\s*Bank Balance\s*:\s*/i, "")}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-6 shrink-0">
          <SummaryCell
            label="Net"
            value={netTotal == null ? "—" : fmtNet(netTotal)}
            colorClass={
              netTotal == null
                ? "text-palette-gray-placeholder"
                : netTotal >= 0
                  ? "text-smiles-green"
                  : "text-smiles-redOrange"
            }
          />
          <SummaryCell
            label="Ending"
            value={endBalance == null ? "—" : fmtBalance(endBalance)}
            colorClass={
              endBalance != null && endBalance < 0
                ? "text-smiles-redOrange"
                : "text-palette-gray-primary"
            }
          />
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────── */}
      {open && (
        <div className="overflow-x-auto">
          <table
            className="cf-table border-collapse text-sm w-full"
            style={
              viewMode !== "weekly"
                ? { minWidth: `${200 + columns.length * 84 + 100}px` }
                : undefined
            }
          >
            <thead>
              <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle">
                <th
                  className={`px-4 py-2 text-left text-xs font-semibold text-palette-gray-secondary uppercase tracking-wide${viewMode !== "weekly" ? " sticky left-0 z-20 bg-palette-gray-subtle min-w-[200px]" : " bg-palette-gray-subtle"}`}
                  style={viewMode === "weekly" ? { width: "200px" } : undefined}
                >
                  Line Item
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-2 py-2 text-right font-medium ${
                      viewMode !== "weekly" ? "min-w-[84px]" : ""
                    } ${col.isPast ? "bg-palette-gray-border" : ""} ${
                      col.isPastEdge ? "border-r-2 border-palette-gray-secondary" : ""
                    }`}
                    title={col.isPast ? "Past — already settled" : undefined}
                  >
                    <div className="text-xs text-palette-gray-secondary">{col.header1}</div>
                    <div className="text-[13px] text-palette-gray-heading">{col.header2}</div>
                  </th>
                ))}
                {/* Period Total */}
                <th
                  className="px-2 py-2 text-right font-medium bg-palette-gray-card border-l border-palette-gray-divider"
                  style={viewMode === "weekly" ? { width: "116px" } : { minWidth: "100px" }}
                >
                  <div className="text-xs font-semibold tracking-wide text-palette-gray-secondary uppercase">
                    Period
                  </div>
                  <div className="text-[13px] font-semibold text-palette-gray-heading">Total</div>
                </th>
              </tr>
            </thead>

            <tbody>
              {rowDefs.map((def, rowIdx) => {
                if (def.type === "child" && def.section === "inflows" && !inflowsOpen) return null;
                if (def.type === "child" && def.section === "outflows" && !outflowsOpen)
                  return null;
                if (def.type === "child" && def.section === "transfers" && !transfersOpen)
                  return null;
                // Transfer group children only show when their group is expanded.
                if (def.role === "groupchild" && !xferGroupsOpen[def.groupId]) return null;

                // Rows this close to the top of the table have no room above
                // them for a breakdown panel (see where it is rendered below).
                const dropBreakdownDown = rowIdx < 2;

                const isInflowSection = def.type === "section" && def.section === "inflows";
                const isOutflowSection = def.type === "section" && def.section === "outflows";
                const isTransferSection = def.type === "section" && def.section === "transfers";
                const isXferGroup = def.role === "group";

                const rowBg =
                  def.type === "balance"
                    ? "bg-palette-gray-subtle"
                    : def.type === "net"
                      ? "bg-palette-indigo-soft"
                      : // A card's header row is shaded like a balance row — it
                        // reports a balance, and the shading separates one card
                        // block from the next. Its children stay plain, the way
                        // the Inflows / Outflows children do.
                        def.role === "cardgroup"
                        ? "bg-palette-gray-subtle"
                        : "bg-white";

                const isSection = isInflowSection || isOutflowSection || isTransferSection;

                const labelCls =
                  def.type === "balance"
                    ? "font-semibold text-palette-gray-primary"
                    : // Credit card block, set at the weight of the Inflows /
                      // Outflows sections: a bold header naming the card, and
                      // full-size indented children under it. Deliberately NOT
                      // the flow colours — green would read as money coming in
                      // and red as money going out, where a card balance is a
                      // position, so it takes the neutral balance colour.
                      def.role === "cardgroup"
                      ? "font-bold text-palette-gray-primary"
                      : def.role === "cardchild"
                        ? "pl-6 text-palette-gray-heading"
                        : def.type === "reference"
                          ? // Deliberately the faintest rows in the grid: they sit
                            // directly under Available Balance and must not read as
                            // more projection lines. Children indent under the
                            // Closing Available parent.
                            `font-normal text-palette-gray-placeholder ${
                              def.role === "refchild" ? "pl-9" : ""
                            }`
                          : isInflowSection
                            ? "font-bold text-smiles-green"
                            : isOutflowSection
                              ? "font-bold text-smiles-redOrange"
                              : isTransferSection
                                ? "font-bold text-palette-indigo-badge"
                                : def.type === "net"
                                  ? "font-bold text-palette-gray-primary"
                                  : def.role === "groupchild"
                                    ? "pl-10 text-palette-gray-heading"
                                    : def.section === "transfers"
                                      ? "pl-6 text-palette-gray-heading"
                                      : def.section === "inflows"
                                        ? "pl-6 text-palette-gray-heading"
                                        : def.section === "outflows"
                                          ? "pl-6 text-palette-gray-heading"
                                          : "text-palette-gray-heading";

                const sectionOpen = isInflowSection
                  ? inflowsOpen
                  : isOutflowSection
                    ? outflowsOpen
                    : transfersOpen;
                const toggleSection = () => {
                  if (isInflowSection) setInflowsOpen((v) => !v);
                  else if (isOutflowSection) setOutflowsOpen((v) => !v);
                  else setTransfersOpen((v) => !v);
                };

                // Period total value for this row
                const totalValue = getCellValue(def.key, def.type, days, enrichedData);

                return (
                  <tr key={def.key} className={`border-b border-palette-gray-divider ${rowBg}`}>
                    <td
                      className={`px-4 text-left ${
                        // A card's name is long ("VISA: RBC PARK LANE DENTAL
                        // [4616]") and this column is 200px. Every other label
                        // is short enough to stay on one line, but forcing this
                        // one to would either widen the column past its
                        // neighbours or hide most of the name behind an
                        // ellipsis — so the card header alone is allowed to
                        // wrap, and shows the full name at full size.
                        def.role === "cardgroup" ? "" : "whitespace-nowrap"
                      } ${
                        // Reference row: tighter and a size down from the real
                        // rows, so it reads as an annotation on the balance
                        // above rather than a line of the statement.
                        def.type === "reference" ? "py-1 text-[11px]" : "py-2 text-sm"
                      } ${viewMode !== "weekly" ? "sticky left-0 z-10" : ""} ${rowBg} ${labelCls}`}
                    >
                      {isSection ? (
                        <button
                          className="flex items-center gap-1.5 font-bold"
                          onClick={toggleSection}
                        >
                          <Chevron open={sectionOpen} className="w-3 h-3" />
                          {def.label}
                        </button>
                      ) : isXferGroup ? (
                        <button
                          className="flex items-center gap-1.5"
                          onClick={() =>
                            setXferGroupsOpen((m) => ({ ...m, [def.groupId]: !m[def.groupId] }))
                          }
                        >
                          <Chevron open={!!xferGroupsOpen[def.groupId]} className="w-3 h-3" />
                          {def.label}
                        </button>
                      ) : def.role === "groupchild" ? (
                        <span className="inline-flex items-center">
                          {def.label}
                          <TransferStatusPill status={def.status} />
                        </span>
                      ) : def.section === "transfers" && def.type === "child" ? (
                        <span className="inline-flex items-center">
                          {def.label}
                          <TransferStatusPill status={def.status} />
                        </span>
                      ) : def.role === "refgroup" ? (
                        <button
                          className="flex items-center gap-1.5 text-left"
                          onClick={() => setClosingOpen((v) => !v)}
                          title={
                            closingOpen
                              ? "Hide the figures behind this"
                              : "Show Closing Balance and Closing LOC Balance"
                          }
                        >
                          <Chevron open={closingOpen} className="w-3 h-3" />
                          <span className="inline-flex flex-col leading-tight">
                            {def.label}
                            <span className="text-[10px] text-palette-gray-border">
                              {def.sublabel}
                            </span>
                          </span>
                        </button>
                      ) : def.role === "cardgroup" ? (
                        <button
                          className="flex items-center gap-1.5 text-left font-bold"
                          onClick={() =>
                            setCardsOpen((m) => ({
                              ...m,
                              [def.cardId]: !(m[def.cardId] ?? true),
                            }))
                          }
                          title={def.label}
                        >
                          <Chevron open={isCardOpen(def.cardId)} className="w-3 h-3" />
                          {/* Capped so the wrapped name stays inside the 200px
                              Line Item column rather than widening it: 200 less
                              the cell's px-4 and the chevron beside it. The
                              sublabel says what kind of row this is, in the same
                              style as the other sublabels. */}
                          <span className="inline-flex min-w-0 max-w-[150px] flex-col leading-tight">
                            {/* The chip sits INSIDE the wrapping name rather
                                than beside it: the column is 200px and the card
                                name already wraps, so a sibling would either
                                widen the column or get squeezed. Inline, it
                                simply flows onto the next line when the name
                                fills the current one. */}
                            <span className="break-words">
                              {def.label}
                              <DueDatePill text={def.badge} />
                            </span>
                            <span className="text-[10px] font-normal text-palette-gray-placeholder">
                              {def.sublabel}
                            </span>
                          </span>
                        </button>
                      ) : def.badge ? (
                        <span className="inline-flex items-center">
                          {def.label}
                          <DueDatePill text={def.badge} />
                        </span>
                      ) : def.sublabel ? (
                        <span className="inline-flex flex-col leading-tight">
                          {def.label}
                          <span className="text-[10px] font-normal text-palette-gray-placeholder">
                            {def.sublabel}
                          </span>
                        </span>
                      ) : (
                        def.label
                      )}
                    </td>

                    {columns.map((col) => {
                      const value = getCellValue(def.key, def.type, col.days, enrichedData);

                      const firstDay = col.days[0];
                      const isEstimateOpening =
                        def.key === "opening" &&
                        value != null &&
                        !enrichedData.openingIsReal?.[firstDay?.date];

                      // Associate-fee cell backed only by expected (not-yet-cleared)
                      // checks: shown muted/gray and NOT deducted from the balance.
                      const expectedFee =
                        def.key === "associateFee" && value != null
                          ? getCellValue("associateFeeExpected", "child", col.days, enrichedData)
                          : null;
                      const isExpectedFee =
                        expectedFee != null && value != null && expectedFee >= value - 0.005;

                      // Checks cell holding checks that have NOT cleared yet:
                      // they're stacked on today because their real clear date
                      // is unknown, so the cell is flagged amber (deliberately
                      // not the outflow red — the money is committed, not gone).
                      const unclearedChecks =
                        def.key === "checks" && value != null
                          ? getCellValue("checksUncleared", "child", col.days, enrichedData)
                          : null;
                      const hasUnclearedChecks = !!unclearedChecks;
                      // Whole cell is uncleared vs. a mix of cleared + uncleared.
                      const allUncleared =
                        hasUnclearedChecks && value != null && unclearedChecks >= value - 0.005;

                      // Scheduled payments that were due before today and never
                      // cleared: reconciliation stacks them on today, so an
                      // outflow cell can be carrying money that has not left the
                      // bank. Flagged like the uncleared checks above, because it
                      // is the same claim — committed, not gone.
                      //
                      // Looked up by `<rowKey>Uncleared`, the convention
                      // `checksUncleared` set, so every outflow row — the named
                      // ones, the shared "Other payments", and the Outflows
                      // total — flags itself without a case per row. `checks` is
                      // excluded because it already has its own treatment above
                      // and would otherwise be reasoned about twice.
                      const unclearedScheduled =
                        def.section === "outflows" && def.key !== "checks" && value != null
                          ? getCellValue(`${def.key}Uncleared`, "child", col.days, enrichedData)
                          : null;
                      const hasRolledScheduled = !!unclearedScheduled;
                      // Whole cell is overdue vs. a mix.
                      const allRolledScheduled =
                        hasRolledScheduled && value != null && unclearedScheduled >= value - 0.005;

                      // The other half: how much of this cell has been matched
                      // to real bank activity. Shown on the cell itself rather
                      // than only in the hover panel — "has this gone out yet"
                      // is the question the grid is being scanned for, and
                      // making it a hover-only detail meant nobody saw it.
                      const clearedScheduled =
                        def.section === "outflows" && def.key !== "checks" && value != null
                          ? getCellValue(`${def.key}Cleared`, "child", col.days, enrichedData)
                          : null;
                      const hasClearedScheduled = !!clearedScheduled;
                      const allClearedScheduled =
                        hasClearedScheduled && value != null && clearedScheduled >= value - 0.005;

                      // How much of that a PERSON has confirmed, as against the
                      // matcher's own reading. A guess and a recorded decision
                      // drew the same emerald tick before this, so a cell said
                      // "cleared" with equal confidence either way — and the
                      // guess is the one that can be wrong.
                      const confirmedScheduled =
                        def.section === "outflows" && def.key !== "checks" && value != null
                          ? getCellValue(`${def.key}Confirmed`, "child", col.days, enrichedData)
                          : null;
                      const allConfirmedScheduled =
                        !!confirmedScheduled &&
                        clearedScheduled != null &&
                        confirmedScheduled >= clearedScheduled - 0.005;

                      // Only allow click-to-move on daily single-day outflow cells
                      const cellOcc =
                        col.days.length === 1 && def.type === "child" && def.section === "outflows"
                          ? occurrences?.[def.key]?.[firstDay?.date]
                          : null;
                      const clickable = onCellClick && cellOcc && cellOcc.length > 0;

                      const semanticBg = isEstimateOpening
                        ? "bg-palette-indigo-soft"
                        : isExpectedFee
                          ? ""
                          : hasUnclearedChecks || hasRolledScheduled
                            ? // Amber wash + left rule: reads as "uncleared",
                              // clearly apart from the red of settled outflows.
                              "bg-palette-amber-soft border-l-2 border-palette-amber-ring"
                            : allClearedScheduled
                              ? // Fully accounted for against the bank. EMERALD
                                // only when a person has confirmed it; sky while
                                // it is still only the matcher's reading, so the
                                // grid never dresses a guess as a settled fact.
                                // No wash either way — a settled cell stays
                                // quiet next to the amber ones needing action.
                                `border-l-2 ${
                                  allConfirmedScheduled
                                    ? "border-palette-emerald-badge"
                                    : "border-palette-sky-badge"
                                } ${getCellBgClass(value, def.type, def.section)}`
                              : getCellBgClass(value, def.type, def.section);
                      // Past days sit on a gray wash. It is only the BASE: a cell
                      // that earned a meaning-carrying colour (estimate, uncleared,
                      // in/out) keeps it, since "which way did the money go" still
                      // matters on a settled day. The gray header band above the
                      // column and the divider at today mark the boundary either way.
                      const cellBg = semanticBg || (col.isPast ? "bg-palette-gray-divider" : "");

                      // Breakdown tooltip, gathered across EVERY day the column
                      // covers — a weekly or collapsed-Past cell is exactly where
                      // an unexplained total is least useful. Balance rows are the
                      // exception: the cell shows ONE day (opening = first, ending
                      // = last), so its breakdown must read that same day rather
                      // than sum a week of balances into a meaningless figure.
                      const breakdownDays =
                        def.type === "balance"
                          ? [
                              def.key === "ending" || def.key === "netsuiteTxnBalance"
                                ? col.days[col.days.length - 1]
                                : col.days[0],
                            ].filter(Boolean)
                          : col.days;
                      const cellBreakdown = collectBreakdown(breakdowns?.[def.key], breakdownDays);
                      // Day order first, then biggest contributor within the
                      // day. A By Company column now carries one line per
                      // clinic PER DAY rather than one per clinic, so sorting
                      // on amount alone would interleave days and the list
                      // would read as a jumble; grouping by day keeps a
                      // multi-day column legible while still leading each day
                      // with what moved the number.
                      if (byClinicBreakdown) {
                        cellBreakdown.sort(
                          (a, b) =>
                            String(a.date).localeCompare(String(b.date)) ||
                            Math.abs(b.amount) - Math.abs(a.amount),
                        );
                      }
                      const hasBreakdown = cellBreakdown.length > 0;
                      // Only the visible slice is put in the DOM. Every cell in
                      // the grid renders its panel up front (hidden until
                      // hover), so an uncapped list — today's Checks cell can
                      // hold hundreds — multiplies into tens of thousands of
                      // nodes per card and is what makes scrolling stutter.
                      const shownBreakdown =
                        cellBreakdown.length > MAX_BREAKDOWN_LINES
                          ? cellBreakdown.slice(0, MAX_BREAKDOWN_LINES)
                          : cellBreakdown;
                      const hiddenBreakdownCount = cellBreakdown.length - shownBreakdown.length;
                      // A line with a `link` (NetSuite checks) has to be
                      // clickable, which the panel's default click-through
                      // mode would swallow.
                      const hasBreakdownLinks = cellBreakdown.some((item) => item.link);
                      // Reconciliation turned payment lists from two short lines
                      // into per-payment evidence — a chip row, a matched
                      // reference, a near-miss figure. The panel below stays
                      // click-through only while it is short enough not to need
                      // scrolling; once it carries this, the mouse has to be
                      // able to reach it.
                      const hasReconDetail = cellBreakdown.some(
                        (item) => item.cleared || item.rolledForwardFrom || item.unclearedReason,
                      );

                      const balanceTitle =
                        def.key === "opening" && value != null
                          ? isEstimateOpening
                            ? "Estimated — carried from the previous day's ending balance"
                            : "Actual — entered in Daily Bank Balance"
                          : undefined;

                      // Expected (not-yet-cleared) associate-fee cell → click to
                      // open a prefilled funding transfer (clinic → SFC).
                      const feeClickable =
                        isExpectedFee && col.days.length === 1 && !!onExpectedFeeClick;
                      // Clicking a cell whose own click is already spoken for
                      // (move a payment / fund fees) keeps that action; every
                      // other cell with a breakdown pins its panel open instead.
                      const cellId = `${def.key}|${col.key}`;
                      const isPinned = pinnedCell === cellId;

                      // Credit card payment → open the statement behind it. Only
                      // when the day resolves to exactly ONE statement: a bank
                      // account can pay two cards on the same day (9 of 188 such
                      // cells today), and there is no honest way to pick one of
                      // them for the user. Those keep the breakdown panel, whose
                      // lines are individually clickable instead.
                      const cardStatementIds =
                        def.key === CARD_PAYMENT_KEY && col.days.length === 1
                          ? [...new Set(cellBreakdown.map((i) => i.statementId).filter(Boolean))]
                          : [];
                      const cardClickable = !!onCardPaymentClick && cardStatementIds.length === 1;
                      const cardDetail = cardClickable
                        ? cellBreakdown.find((i) => i.statementId === cardStatementIds[0])
                        : null;

                      const pinnable =
                        hasBreakdown && !clickable && !feeClickable && !cardClickable;
                      const cellClick = clickable
                        ? () =>
                            onCellClick({
                              code,
                              rowKey: def.key,
                              day: firstDay.day,
                              occurrences: cellOcc,
                            })
                        : cardClickable
                          ? () =>
                              onCardPaymentClick({
                                statementId: cardDetail.statementId,
                                // `{ card: { id } }`, not a flat id: that is the
                                // shape openCardStatementEditor already takes
                                // from CardStatementPanel.
                                card: { id: cardDetail.cardAccountId ?? null },
                              })
                          : feeClickable
                            ? () =>
                                onExpectedFeeClick({
                                  code,
                                  clinicName,
                                  date: firstDay.date,
                                  amount: value,
                                })
                            : pinnable
                              ? () => setPinnedCell((prev) => (prev === cellId ? null : cellId))
                              : undefined;

                      return (
                        <td
                          key={col.key}
                          data-breakdown-cell={pinnable ? cellId : undefined}
                          onClick={cellClick}
                          title={
                            clickable
                              ? "Click to change this payment's date or amount"
                              : cardClickable
                                ? "Click to open this credit card statement"
                                : feeClickable
                                  ? "Click to fund these associate fees (transfer to SFC)"
                                  : hasUnclearedChecks
                                    ? `${allUncleared ? "All" : "Some"} of these checks have not cleared yet — shown on today because their clear date is unknown`
                                    : hasRolledScheduled
                                      ? `${allRolledScheduled ? "All" : "Some"} of this was due earlier and has not cleared`
                                      : pinnable
                                        ? "Click to keep this breakdown open"
                                        : balanceTitle
                          }
                          className={`relative group px-2 text-right tabular-nums ${
                            def.type === "reference" ? "py-1 text-[11px]" : "py-2"
                          } ${cellBg} ${
                            clickable || cardClickable
                              ? "cursor-pointer underline decoration-dotted underline-offset-2 hover:bg-palette-indigo-soft"
                              : feeClickable
                                ? "cursor-pointer underline decoration-dotted underline-offset-2 hover:bg-palette-indigo-soft"
                                : pinnable
                                  ? "cursor-pointer hover:bg-palette-indigo-soft"
                                  : ""
                          } ${isPinned ? "bg-palette-indigo-soft" : ""} ${
                            col.isPastEdge ? "border-r-2 border-palette-gray-secondary" : ""
                          }`}
                        >
                          <CellValue
                            value={value}
                            rowType={def.type}
                            section={def.section}
                            estimate={isEstimateOpening}
                            muted={isExpectedFee}
                            uncleared={hasUnclearedChecks || hasRolledScheduled}
                          />
                          {hasUnclearedChecks && !allUncleared && (
                            // A mixed cell: say how much of the total is still
                            // outstanding, since the amount alone can't.
                            <div className="text-[10px] leading-tight text-palette-amber-heading">
                              {fmtBalance(unclearedChecks)} uncleared
                            </div>
                          )}
                          {hasRolledScheduled && (
                            // On a mixed cell the total cannot say how much of
                            // it is overdue rather than actually going out
                            // today; on a fully-overdue one it still needs to
                            // say that none of this is due HERE — the amount
                            // alone looks like a payment scheduled for this day.
                            <div className="text-[10px] leading-tight text-palette-amber-heading">
                              {allRolledScheduled
                                ? "all carried from earlier days"
                                : `${fmtBalance(unclearedScheduled)} carried from earlier days`}
                            </div>
                          )}
                          {hasClearedScheduled && (
                            <div
                              className={`text-[10px] leading-tight ${
                                allConfirmedScheduled
                                  ? "text-palette-emerald-text"
                                  : "text-palette-sky-text"
                              }`}
                              title={
                                allConfirmedScheduled
                                  ? "Confirmed against the bank by a person."
                                  : "Matched automatically on amount and date — nobody has confirmed it yet. " +
                                    "Use “Confirm matches” to record a decision."
                              }
                            >
                              {allConfirmedScheduled
                                ? allClearedScheduled
                                  ? "✓ cleared"
                                  : `✓ ${fmtBalance(clearedScheduled)} cleared`
                                : // "likely" is the honest word for a match
                                  // nobody has ruled on: it is amount-and-date
                                  // matching, and it will be wrong sometimes.
                                  // Confirming turns this into the tick above.
                                  allClearedScheduled
                                  ? "≈ likely cleared"
                                  : `≈ ${fmtBalance(clearedScheduled)} likely cleared`}
                            </div>
                          )}
                          {hasBreakdown && (
                            <div
                              data-breakdown-panel={isPinned ? cellId : undefined}
                              // An interactive panel overlaps its neighbours,
                              // so a click aimed at one of them lands here
                              // instead. Pinning is the useful thing to do with
                              // that click — better than swallowing it — and it
                              // must not also fire the cell's own action
                              // underneath (which would move a payment).
                              onClick={
                                hasReconDetail && !isPinned
                                  ? (e) => {
                                      e.stopPropagation();
                                      setPinnedCell(cellId);
                                    }
                                  : undefined
                              }
                              className={`absolute left-1/2 -translate-x-1/2 rounded-lg border bg-white p-2 text-left shadow-lg ${
                                // Pinned: stays open (and on top of any hovered
                                // neighbour) until dismissed. Otherwise it is the
                                // plain hover panel.
                                isPinned
                                  ? "z-40 block border-palette-indigo-border ring-1 ring-palette-indigo-border"
                                  : "z-30 hidden border-palette-gray-divider group-hover:block"
                              } ${
                                // The scroll container clips vertically, so a
                                // panel opening upward from one of the top rows
                                // would be cut off — those drop downward instead.
                                dropBreakdownDown ? "top-full mt-1" : "bottom-full mb-1"
                              } ${
                                // A clinic list runs as long as the selection
                                // does, so it stays interactive: the panel is
                                // hovering INSIDE the cell, so the mouse can
                                // move onto it and scroll the list without it
                                // closing. Payment lists are short and stay
                                // click-through, so they never sit between the
                                // cursor and a clickable cell.
                                byClinicBreakdown
                                  ? "w-96 pointer-events-auto"
                                  : isPinned
                                    ? "w-[30rem] pointer-events-auto"
                                    : hasBreakdownLinks || hasReconDetail
                                      ? "w-96 pointer-events-auto"
                                      : "w-96 pointer-events-none"
                              }`}
                            >
                              <div className="mb-1 flex items-center justify-between gap-2 border-b border-palette-gray-divider pb-1 text-[10px] font-semibold uppercase tracking-wider text-palette-gray-secondary">
                                <span className="truncate">{def.label}</span>
                                {/* Pin, for a panel the CELL cannot pin: an
                                    outflow cell's own click moves the payment,
                                    so `pinnable` is false there — and that is
                                    exactly the cell carrying reconciliation
                                    detail. Without this the list can only be
                                    read while the cursor stays inside it, and
                                    the wheel scrolls the table out from under
                                    it instead of scrolling the list. */}
                                {!isPinned && (hasReconDetail || hasBreakdownLinks) && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPinnedCell(cellId);
                                    }}
                                    title="Keep this open"
                                    className="shrink-0 rounded px-1 text-[11px] leading-none text-palette-gray-placeholder hover:bg-palette-gray-subtle hover:text-palette-gray-heading"
                                  >
                                    Pin
                                  </button>
                                )}
                                {isPinned && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPinnedCell(null);
                                    }}
                                    title="Close (Esc)"
                                    className="shrink-0 rounded px-1 text-sm leading-none text-palette-gray-placeholder hover:bg-palette-gray-subtle hover:text-palette-gray-heading"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                              {/* Height-capped and scrollable in BOTH axes: a
                                  cell can hold dozens of checks, and a long memo
                                  runs past the panel width — truncating it hid
                                  exactly the part that identifies the payment.
                                  Lines keep their full width (min-w-max) and the
                                  container scrolls sideways to reach it.
                                  `overscroll-contain` keeps the wheel inside the
                                  list instead of scrolling the table behind. */}
                              <div
                                className={`overflow-auto overscroll-contain ${
                                  isPinned ? "max-h-80" : "max-h-64"
                                }`}
                              >
                                {shownBreakdown.map((item, i) => {
                                  // A payment line that names its statement opens
                                  // it. This is what serves the cells the whole-cell
                                  // click cannot: a day paying two cards has no single
                                  // statement to open, but each LINE has exactly one.
                                  const lineOpensStatement =
                                    !!onCardPaymentClick && !!item.statementId;
                                  return (
                                    <div
                                      key={i}
                                      onClick={
                                        lineOpensStatement
                                          ? (e) => {
                                              // The cell underneath pins the panel;
                                              // opening the statement must not also
                                              // close the thing being read.
                                              e.stopPropagation();
                                              onCardPaymentClick({
                                                statementId: item.statementId,
                                                card: { id: item.cardAccountId ?? null },
                                              });
                                            }
                                          : undefined
                                      }
                                      title={
                                        lineOpensStatement
                                          ? "Click to open this credit card statement"
                                          : undefined
                                      }
                                      className={`py-0.5 text-xs ${
                                        lineOpensStatement
                                          ? "cursor-pointer rounded hover:bg-palette-indigo-soft"
                                          : ""
                                      }`}
                                    >
                                      {/* Label and amount on their own row, so
                                        the amount stays where the eye expects
                                        it. The chips go BELOW (see ReconChips)
                                        rather than inline: inline they pushed
                                        the amount off the right edge of a
                                        min-w-max line and the panel could only
                                        be read by scrolling sideways. */}
                                      <div className="flex min-w-max items-start justify-between gap-4">
                                        <span
                                          className={`text-palette-gray-heading ${
                                            // Clinic names are the whole point of
                                            // the By Company list, so they wrap in
                                            // full rather than being cut off
                                            // mid-name. Payment/check labels stay
                                            // on one line and are reached by
                                            // scrolling sideways, so a long memo is
                                            // readable in full.
                                            byClinicBreakdown
                                              ? "min-w-0 break-words"
                                              : "whitespace-nowrap"
                                          }`}
                                        >
                                          {/* The date only earns its place on a
                                          multi-day column; on a single day it
                                          would just repeat the header. Clinic
                                          lines carry it too now that they are
                                          one per day rather than one per column
                                          — without it the same clinic name
                                          appears several times with no way to
                                          tell the entries apart.
                                          EXCEPT for a carried-forward payment:
                                          it sits on a cell that is NOT its own
                                          date, so the header no longer says when
                                          it was due — the one thing the reader
                                          needs. */}
                                          {!byClinicBreakdown && item.rolledForwardFrom ? (
                                            <span
                                              className="mr-1 font-semibold text-palette-amber-heading"
                                              title={`Due ${item.rolledForwardFrom}. That date is NOT changed by being shown here.`}
                                            >
                                              due {fmtBreakdownDate(item.rolledForwardFrom)} →
                                            </span>
                                          ) : (
                                            col.days.length > 1 && (
                                              <span className="mr-1 text-palette-gray-placeholder">
                                                {fmtBreakdownDate(item.date)}
                                              </span>
                                            )
                                          )}
                                          {item.link ? (
                                            // stopPropagation: the cell underneath
                                            // may itself be clickable (move a
                                            // payment), and opening NetSuite should
                                            // not also trigger it.
                                            <a
                                              href={item.link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              title="Open in NetSuite"
                                              className="text-palette-indigo-heading underline decoration-dotted underline-offset-2 hover:decoration-solid"
                                            >
                                              {item.label}
                                            </a>
                                          ) : (
                                            item.label
                                          )}
                                        </span>
                                        <span className="shrink-0 font-semibold tabular-nums text-palette-gray-primary">
                                          {fmtBalance(item.amount)}
                                        </span>
                                      </div>
                                      <ReconChips item={item} />
                                    </div>
                                  );
                                })}
                                {hiddenBreakdownCount > 0 && (
                                  <div className="py-0.5 text-xs italic text-palette-gray-placeholder">
                                    +{hiddenBreakdownCount} more (total below)
                                  </div>
                                )}
                              </div>
                              {/* Only worth a total when the cell is a sum. A
                                  balance row is a point-in-time figure, not a
                                  sum of its lines — but for the By Company view
                                  it IS the sum of each clinic's balance, so the
                                  total still holds. */}
                              {cellBreakdown.length > 1 && (
                                <div className="mt-1 flex items-center justify-between gap-2 border-t border-palette-gray-divider pt-1 text-xs font-semibold">
                                  <span className="text-palette-gray-secondary">
                                    {/* "clinics" only holds while a column
                                        is one day — a multi-day column lists a
                                        clinic once per day it moved, so the
                                        count is of entries, not of clinics. */}
                                    {cellBreakdown.length}{" "}
                                    {byClinicBreakdown
                                      ? col.days.length > 1
                                        ? "entries"
                                        : "clinics"
                                      : "payments"}
                                  </span>
                                  <span className="tabular-nums text-palette-gray-primary">
                                    {fmtBalance(
                                      cellBreakdown.reduce(
                                        (s, x) => s + (Number(x.amount) || 0),
                                        0,
                                      ),
                                    )}
                                  </span>
                                </div>
                              )}
                              {/* A hover panel is click-through, so its list
                                  can't be scrolled — say so when there is more
                                  below the fold than the cap shows. */}
                              {!isPinned && pinnable && cellBreakdown.length > 8 && (
                                <div className="mt-1 text-[10px] italic text-palette-gray-placeholder">
                                  Click the cell to scroll the full list
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}

                    {/* Period Total cell */}
                    <td
                      className={`px-2 text-right tabular-nums border-l border-palette-gray-divider bg-palette-gray-card ${
                        def.type === "reference" ? "py-1 text-[11px]" : "py-2"
                      }`}
                    >
                      <CellValue
                        value={totalValue}
                        rowType={def.type}
                        section={def.section}
                        estimate={false}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Card statement panels ───────────────────────────────
          Below the table, in card order, so each one sits under the rows it
          belongs to. Outside the scroll container on purpose: the panel is
          prose and lists, not day columns, and it should not slide sideways
          when the grid is scrolled. */}
      {open &&
        creditCards.map((c) =>
          cardDetails[c.key] ? (
            <CardStatementPanel
              key={`panel-${c.key}`}
              detail={cardDetails[c.key]}
              onEditCharge={onEditCardCharge}
              onEditStatement={onEditCardStatement}
            />
          ) : null,
        )}
    </div>
  );
}
